import { gql } from "@apollo/client";
import { client } from "../graphql/client";
import { RoleType } from "graphql/types/AccountType";
import { removeUserResourceCodes, hasResource } from "./resourceAuth";
import { removeMyDataPortalPages } from "./dataPortalCache";
import { ResourceCode } from "../constants/resourceCodes";
import { getOrCreateDeviceId, getEndType } from "./deviceId";
import { getStoredLanguage } from "./languagePreference";

// 存储和获取认证令牌
const USER_INFO_KEY = 'swiftx_user_info';

/**
 * 应用程序角色常量定义
 * 与后端 AppRole 枚举保持一致
 */
export const AppRoleConstants = {
  WAREHOUSE_MANAGER: 'WAREHOUSE_MANAGER' as const,
  WAREHOUSE_OPERATOR: 'WAREHOUSE_OPERATOR' as const,
  DSP_MANAGER: 'DSP_MANAGER' as const,
  DELIVERY_DRIVER: 'DELIVERY_DRIVER' as const,
  HUB_MANAGER: 'HUB_MANAGER' as const,
  HUB_OPERATOR: 'HUB_OPERATOR' as const,
  PICKUP_STAFF: 'PICKUP_STAFF' as const,
  PICKUP_DISPATCHER: 'PICKUP_DISPATCHER' as const,
  PICKUP_DRIVER: 'PICKUP_DRIVER' as const,
  HEADQUARTERS_STAFF: 'HEADQUARTERS_STAFF' as const,
  HEADQUARTERS_ADMIN: 'HEADQUARTERS_ADMIN' as const,
  KAQC: 'KAQC' as const,
  FINANCIAL_TEAM: 'FINANCIAL_TEAM' as const,
  CS: 'CS' as const,
  TMS_DISPATCHER: 'TMS_DISPATCHER' as const,
  TMS_DRIVER: 'TMS_DRIVER' as const,
  CONFIG_ADMIN: 'CONFIG_ADMIN' as const,
  CS_FOLLOW_UP: 'CS_FOLLOW_UP' as const,
  RISK_CONTROL: 'RISK_CONTROL' as const,
  DSP_FINANCIAL_TEAM: 'DSP_FINANCIAL_TEAM' as const,
  INCOME_FINANCIAL_TEAM: 'INCOME_FINANCIAL_TEAM' as const,
  DSP_OWNER_TEAM: 'DSP_OWNER_TEAM' as const,
  SORTING_STAFF: 'SORTING_STAFF' as const,
} as const;

export interface UserInfo {
  token: string;
  refreshToken?: string;
  account: {
    id: string;
    name: string;
    language?: string | null;
    role: {
      name: RoleType;
      permissions: string[];
    };
  };
}

// 保存用户信息
export const saveUserInfo = (userInfo: UserInfo): void => {
  localStorage.setItem(USER_INFO_KEY, JSON.stringify(userInfo));
};

// 获取用户信息
export const getUserInfo = (): UserInfo | null => {
  const userInfoStr = localStorage.getItem(USER_INFO_KEY);
  if (userInfoStr) {
    try {
      return JSON.parse(userInfoStr);
    } catch (e) {
      removeUserInfo();
      return null;
    }
  }
  return null;
};

export const getUserRole = (): RoleType => {
  const userInfo = getUserInfo();
  if (!userInfo) {
    throw new Error('User info not found');
  }
  return userInfo.account.role.name;
};

const _isWarehouseManager = (userInfo: UserInfo | null): boolean => {
  return userInfo?.account.role.name === 'WAREHOUSE_MANAGER';
};

export const isWarehouseManager = (): boolean => {
  const userInfo = getUserInfo();
  return _isWarehouseManager(userInfo);
};

const _isHubManager = (userInfo: UserInfo | null): boolean => {
  return userInfo?.account.role.name === 'HUB_MANAGER';
};

export const isHubManager = (): boolean => {
  const userInfo = getUserInfo();
  return _isHubManager(userInfo);
};

const _isWarehouseOperator = (userInfo: UserInfo | null): boolean => {
  return userInfo?.account.role.name === 'WAREHOUSE_OPERATOR';
};

export const isWarehouseOperator = (): boolean => {
  const userInfo = getUserInfo();
  return _isWarehouseOperator(userInfo);
};

const _isHubOperator = (userInfo: UserInfo | null): boolean => {
  return userInfo?.account.role.name === 'HUB_OPERATOR';
};

export const isHubOperator = (): boolean => {
  const userInfo = getUserInfo();
  return _isHubOperator(userInfo);
};

const _isSortingStaff = (userInfo: UserInfo | null): boolean => {
  return userInfo?.account.role.name === 'SORTING_STAFF';
};

export const isSortingStaff = (): boolean => {
  const userInfo = getUserInfo();
  return _isSortingStaff(userInfo);
};

export const isPickupDispatcher = (): boolean => {
  const userInfo = getUserInfo();
  return userInfo?.account.role.name === 'PICKUP_DISPATCHER';
};

export const isDspManager = (): boolean => {
  const userInfo = getUserInfo();
  return userInfo?.account.role.name === 'DSP_MANAGER';
};

export const isDspOwnerTeam = (): boolean => {
  const userInfo = getUserInfo();
  return userInfo?.account.role.name === 'DSP_OWNER_TEAM';
};

export const isDeliveryDriver = (): boolean => {
  const userInfo = getUserInfo();
  return userInfo?.account.role.name === 'DELIVERY_DRIVER';
};

export const isHeadquartersStaff = (): boolean => {
  const userInfo = getUserInfo();
  return userInfo?.account.role.name === 'HEADQUARTERS_STAFF';
};

export const hasHeadquartersAccess = (): boolean => {
  const userInfo = getUserInfo();
  const roleName = userInfo?.account.role.name;
  return roleName === 'HEADQUARTERS_STAFF' || roleName === 'HEADQUARTERS_ADMIN';
};

export const hasActualDimensionEditPermission = (): boolean => {
  const userInfo = getUserInfo();
  const roleName = userInfo?.account.role.name;
  return roleName === 'HEADQUARTERS_STAFF' || roleName === 'HEADQUARTERS_ADMIN' || hasResource([
    ResourceCode.menu.HUB_PACKAGE_DETAIL,
    ResourceCode.menu.WHS_PACKAGE_DETAIL,
    ResourceCode.menu.HUB_EXCEPTION_DETAIL,
    ResourceCode.menu.WHS_EXCEPTION_DETAIL,
    ResourceCode.menu.HUB_LABEL_SEARCH_DETAIL,
    ResourceCode.menu.WHS_LABEL_SEARCH_DETAIL,
  ]);
};

export const hasRiskControlAccess = (): boolean => {
  const userInfo = getUserInfo();
  const roleName = userInfo?.account.role.name;
  return roleName === 'RISK_CONTROL' || roleName === 'HEADQUARTERS_ADMIN' || roleName === 'HEADQUARTERS_STAFF';
};

export const isHeadquartersAdmin = (): boolean => {
  const userInfo = getUserInfo();
  return userInfo?.account.role.name === 'HEADQUARTERS_ADMIN';
};

export const isKAQC = (): boolean => {
  const userInfo = getUserInfo();
  return userInfo?.account.role.name === 'KAQC';
};

export const isFinancialTeam = (): boolean => {
  const userInfo = getUserInfo();
  return userInfo?.account.role.name === 'FINANCIAL_TEAM';
};

export const isDspFinancialTeam = (): boolean => {
  const userInfo = getUserInfo();
  return userInfo?.account.role.name === 'DSP_FINANCIAL_TEAM';
};

export const isIncomeFinancialTeam = (): boolean => {
  const userInfo = getUserInfo();
  return userInfo?.account.role.name === 'INCOME_FINANCIAL_TEAM';
};

/** 客户管理页 Settlement Configuration：财务团队 + 总部管理员 */
export const canManageChannelSettlement = (): boolean => {
  return isFinancialTeam() || isIncomeFinancialTeam() || isHeadquartersAdmin();
};

/** DSP 管理页结算配置：财务团队 + 总部管理员 */
export const canManageDspSettlement = (): boolean => {
  return isFinancialTeam() || isDspFinancialTeam() || isHeadquartersAdmin();
};

/**
 * 总部下载列表：后端强制仅可查/下本人生成的报表
 */
export const isHeadquartersDownloadSelfOnlyRole = (): boolean => {
  return (
    hasHeadquartersAccess() ||
    isFinancialTeam() ||
    isDspFinancialTeam() ||
    isIncomeFinancialTeam()
  );
};

/**
 * 仓库侧「下载」列表：后端强制本仓且仅本人
 */
export const isWarehouseDownloadSelfOnlyRole = (): boolean => {
  if (isWarehouseManager() || isHubManager() || isWarehouseOperator() || isHubOperator()) {
    return true;
  }
  const hasDownloadAccess = hasResource([ResourceCode.menu.HUB_DOWNLOAD, ResourceCode.menu.WHS_DOWNLOAD]);
  const hasViewAll = hasResource([ResourceCode.menu.HUB_DOWNLOAD_VIEW_ALL, ResourceCode.menu.WHS_DOWNLOAD_VIEW_ALL]);
  return hasDownloadAccess && hasViewAll;
};

/**
 * 客服系统「下载记录」：后端 getReportFileListingForCs 强制 operatorId = 当前账号，
 * 客服账号 appRole 为 DEFAULT，旧角色判据全部落空，故按资源码判定
 */
export const isCsDownloadSelfOnlyRole = (): boolean => {
  return hasResource([ResourceCode.cs.CS_DOWNLOAD]);
};

/** 报表下载：总部 / 仓库 / 客服角色均由后端锁定为仅本人 */
export const isReportDownloadSelfOnlyRole = (): boolean => {
  return (
    isHeadquartersDownloadSelfOnlyRole() ||
    isWarehouseDownloadSelfOnlyRole() ||
    isCsDownloadSelfOnlyRole()
  );
};

/**  CRM「入驻资料」下载：仅财务团队、收入侧财务可用 */
export const canDownloadChannelOnboardingDocuments = (): boolean => {
  return isFinancialTeam() || isIncomeFinancialTeam();
};

/** 轨迹批量导出、总部包裹外部轨迹：总部员工/管理员 + 财务团队 / DSP 财务 / 收入侧财务 */
export const hasTrackExportAccess = (): boolean => {
  return (
    hasHeadquartersAccess() ||
    isFinancialTeam() ||
    isDspFinancialTeam() ||
    isIncomeFinancialTeam()
  );
};

export const isCS = (): boolean => {
  const userInfo = getUserInfo();
  return userInfo?.account.role.name === 'CS';
};

export const isCSFollowUp = (): boolean => {
  const userInfo = getUserInfo();
  return userInfo?.account.role.name === 'CS_FOLLOW_UP';
};

export const isTmsDispatcher = (): boolean => {
    const userInfo = getUserInfo();
    return userInfo?.account.role.name === 'TMS_DISPATCHER';
};

export const isConfigAdmin = (): boolean => {
    const userInfo = getUserInfo();
    return userInfo?.account.role.name === 'CONFIG_ADMIN';
};

export const isRiskControl = (): boolean => {
    const userInfo = getUserInfo();
    return userInfo?.account.role.name === 'RISK_CONTROL';
};

// 判断是否已登录
export const isLoggedIn = (): boolean => {
  const userInfo = getUserInfo();
  return !!userInfo && !!userInfo.token;
};

export const hasPdaPermission = (userInfo: UserInfo): boolean => {
  if (_isWarehouseManager(userInfo) || _isWarehouseOperator(userInfo)) {
    return true;
  }
  return false;
}

export const getToken = (): string | null => {
  const userInfo = getUserInfo();
  return userInfo?.token ?? null;
};

export const getRefreshToken = (): string | null => {
  const userInfo = getUserInfo();
  return userInfo?.refreshToken ?? null;
};

// 按登录用户 id 隔离仓库选择的 localStorage key；拿不到 userId（理论上不会发生，因为该页面在登录态路由之后）
// 时返回 null，调用方应跳过 localStorage 读写，而不是退回到无用户隔离的旧 key
export const getWmsWarehouseStorageKey = (): string | null => {
  const userId = getUserInfo()?.account.id;
  return userId ? `wms_user_${userId}_selected_warehouse_id` : null;
};

// 按登录用户 id 隔离 TMS 选中 Hub 的 localStorage key，规则同 getWmsWarehouseStorageKey
export const getTmsHubStorageKey = (): string | null => {
  const userId = getUserInfo()?.account.id;
  return userId ? `tms_user_${userId}_selected_hub_id` : null;
};

// 删除用户信息
export const removeUserInfo = (): void => {
  localStorage.removeItem(USER_INFO_KEY);
};

const LOGOUT_MUTATION = gql`
mutation LogoutMutation($input: LogoutInput) {
  logout(input: $input) {
    success
    message
  }
}`;

// 登出
export const logout = async (redirectTo: string = '/login'): Promise<void> => {
  try {
    await client.mutate({
      mutation: LOGOUT_MUTATION,
    });
  } catch {
    // 登出接口调用失败不影响本地清理
  }
  removeUserResourceCodes();
  // 必须在 removeUserInfo 之前清：缓存 key 带 userId，用户信息删了就定位不到要清哪一条
  // 不需要额外清 zustand store：下面 window.location.href 会整页重载，store 随之销毁
  removeMyDataPortalPages();
  removeUserInfo();
  window.location.href = redirectTo;
};

interface LoginResponse {
  login: {
    success: boolean;
    message?: string | null;
    token?: string | null;
    refreshToken?: string | null;
    account?: UserInfo['account'] | null;
    requireKickConfirm?: boolean | null;
    existingDeviceLoginTime?: string | null;
    existingDeviceUserAgent?: string | null;
    displayMessage?: string | null;
  };
}

interface LoginInput {
  countryCode: number;
  phoneOrEmail: string;
  password: string;
  deviceId?: string | null;
  endType?: string | null;
  confirmKickout?: boolean | null;
}

interface LoginVariables {
  input: LoginInput;
}

const LOGIN_MUTATION = gql`
mutation LoginMutation($input: LoginInput!) {
  login(input: $input) {
    success
    message
    token
    refreshToken
    account {
      id
      name
      language
      role {
        name
        permissions
      }
    }
    requireKickConfirm
    existingDeviceLoginTime
    existingDeviceUserAgent
    displayMessage
  }
}`;

/**
 * 登录
 * @param countryCode
 * @param phoneOrEmail 手机号或邮箱
 * @param password 密码
 * @returns userInfo 成功时不为空；messageKey 失败时承载后端国际化 key
 * @throws 网络/GraphQL 异常
 */
export const login = async (
  countryCode: number,
  phoneOrEmail: string,
  password: string,
  confirmKickout: boolean = false,
): Promise<{
  userInfo: UserInfo | null;
  messageKey?: string | null;
  requireKickConfirm?: boolean;
  existingDeviceLoginTime?: string | null;
  existingDeviceUserAgent?: string | null;
  displayMessage?: string | null;
}> => {
  // i18next 当前语言（LanguageDetector 以 swiftx_language 为 localStorage 缓存 key，切换语言即写入），
  // 供后端渲染锁定提示文案；无存储值时不上送，由后端回退浏览器 Accept-Language。
  // 只挂在登录请求上，不放全局 authLink，避免影响其它读该 header 的接口（如订单池导出表头语言）
  const language = getStoredLanguage();
  const { data, errors } = await client.mutate<LoginResponse, LoginVariables>({
    mutation: LOGIN_MUTATION,
    variables: {
      input: {
        countryCode,
        phoneOrEmail,
        password,
        // F3 单会话：上送当前浏览器 deviceId 与端类型
        deviceId: getOrCreateDeviceId(),
        endType: getEndType(),
        confirmKickout,
      },
    },
    context: language ? { headers: { language } } : undefined,
  });
  if (errors) {
    throw errors[0];
  }
  const result = data?.login;
  if (result?.success && result.token && result.account) {
    return {
      userInfo: {
        token: result.token,
        refreshToken: result.refreshToken ?? undefined,
        account: result.account,
      },
      messageKey: null,
    };
  }
  return {
    userInfo: null,
    messageKey: result?.message ?? null,
    requireKickConfirm: result?.requireKickConfirm ?? false,
    existingDeviceLoginTime: result?.existingDeviceLoginTime ?? null,
    existingDeviceUserAgent: result?.existingDeviceUserAgent ?? null,
    displayMessage: result?.displayMessage ?? null,
  };
};